import { GrahaName, RasiName, NakshatraName } from '../../models/types';
import { 
  normalizeDecimalDegrees,
  getRashiIndex,
  getNakshatraIndex,
  getNavamsaIndex,
  getDwadasamsaIndex
} from './coordinates';

/**
 * Utility functions for astrological calculations
 */

/**
 * Calculates the ruler of a given nakshatra
 * @param nakshatraIndex - Index of the nakshatra (0-26)
 * @returns The ruling planet (graha)
 */
export function getNakshatraRuler(nakshatraIndex: number): GrahaName {
  // The traditional Vimshottari dasha order of planets
  const dashaOrder = [
    GrahaName.Ketu,  // Ketu
    GrahaName.Sk,    // Shukra
    GrahaName.Sy,    // Surya
    GrahaName.Ch,    // Chandra
    GrahaName.Ma,    // Mangala
    GrahaName.Rahu,  // Rahu
    GrahaName.Gu,    // Guru
    GrahaName.Sa,    // Shani
    GrahaName.Bu     // Budha
  ];
  
  // Normalize index to 0-26
  const normalizedIndex = ((nakshatraIndex % 27) + 27) % 27;
  
  // Get the ruler based on the Vimshottari dasha cycle
  return dashaOrder[normalizedIndex % 9];
}

/**
 * Gets the nakshatra lord (ruling planet) for a given degree
 * @param longitude - Longitude in decimal degrees
 * @returns The ruling planet (graha)
 */
export function getNakshatraLord(longitude: number): GrahaName {
  const normalized = normalizeDecimalDegrees(longitude);
  const nakshatraIndex = getNakshatraIndex(normalized);
  return getNakshatraRuler(nakshatraIndex);
}

/**
 * Gets the rashi (zodiac sign) name for a given degree
 * @param longitude - Longitude in decimal degrees
 * @returns Rashi name
 */
export function getRashi(longitude: number): RasiName {
  const normalized = normalizeDecimalDegrees(longitude);
  const rashiIndex = getRashiIndex(normalized);
  
  // Return the rashi name at the calculated index
  return Object.values(RasiName)[rashiIndex];
}

/**
 * Gets the nakshatra name for a given degree
 * @param longitude - Longitude in decimal degrees
 * @returns Nakshatra name
 */
export function getNakshatra(longitude: number): NakshatraName {
  const normalized = normalizeDecimalDegrees(longitude);
  const nakshatraIndex = getNakshatraIndex(normalized);
  
  // Return the nakshatra name at the calculated index
  return Object.values(NakshatraName)[nakshatraIndex];
}

/**
 * Gets the navamsa rashi (zodiac sign in the D9 chart) for a given degree
 * @param longitude - Longitude in decimal degrees
 * @returns Navamsa rashi name
 */
export function getNavamsaRashi(longitude: number): RasiName {
  const normalized = normalizeDecimalDegrees(longitude);
  const rashiIndex = getRashiIndex(normalized);
  const navamsaIndex = getNavamsaIndex(normalized);
  
  // Calculate the navamsa rashi index
  // The D9 chart follows a specific pattern based on the original sign
  const rashiTriplets = Math.floor(rashiIndex / 3); // 0 for fire, 1 for earth, etc.
  const navamsaRashiIndex = (rashiTriplets * 3 + navamsaIndex) % 12;
  
  // Return the rashi name at the calculated navamsa index
  return Object.values(RasiName)[navamsaRashiIndex];
}

/**
 * Gets the dwadasamsa rashi (zodiac sign in the D12 chart) for a given degree
 * @param longitude - Longitude in decimal degrees
 * @returns Dwadasamsa rashi name
 */
export function getDwadasamsaRashi(longitude: number): RasiName {
  const normalized = normalizeDecimalDegrees(longitude);
  const rashiIndex = getRashiIndex(normalized);
  const dwadasamsaIndex = getDwadasamsaIndex(normalized);
  
  // Calculate the dwadasamsa rashi index
  // Each rashi is divided into 12 parts, each ruled by a sign starting from itself
  const dwadasamsaRashiIndex = (rashiIndex + dwadasamsaIndex) % 12;
  
  // Return the rashi name at the calculated dwadasamsa index
  return Object.values(RasiName)[dwadasamsaRashiIndex];
}

/**
 * Rulership mapping - centralized for reuse
 */
const RASHI_RULERSHIP: Record<GrahaName, RasiName[]> = {
  [GrahaName.Sy]: [RasiName.Simha], // Surya - Leo
  [GrahaName.Ch]: [RasiName.Karka], // Chandra - Cancer
  [GrahaName.Ma]: [RasiName.Mesha, RasiName.Vrishchika], // Mangala - Aries, Scorpio
  [GrahaName.Bu]: [RasiName.Mithuna, RasiName.Kanya], // Budha - Gemini, Virgo
  [GrahaName.Gu]: [RasiName.Dhanu, RasiName.Meena], // Guru - Sagittarius, Pisces
  [GrahaName.Sk]: [RasiName.Vrishabha, RasiName.Tula], // Shukra - Taurus, Libra
  [GrahaName.Sa]: [RasiName.Makara, RasiName.Kumbha], // Shani - Capricorn, Aquarius
  [GrahaName.Rahu]: [], // No rulership in traditional Vedic astrology
  [GrahaName.Ketu]: [], // No rulership in traditional Vedic astrology
  [GrahaName.Lg]: [], // Lagna is not a planet
};

/**
 * Gets the ruler (graha) of a rashi by index (0-11)
 * @param rashiIndex - Rashi index (0=Mesha, 1=Vrishabha, ..., 11=Meena)
 * @returns The ruling planet (graha)
 */
export function getRashiRuler(rashiIndex: number): GrahaName {
  // Normalize to 0-11
  const normalizedIndex = ((rashiIndex % 12) + 12) % 12;

  // Rashi rulers in order (0=Mesha to 11=Meena)
  const rulers = [
    GrahaName.Ma,  // 0: Mesha (Aries) - Mars
    GrahaName.Sk,  // 1: Vrishabha (Taurus) - Venus
    GrahaName.Bu,  // 2: Mithuna (Gemini) - Mercury
    GrahaName.Ch,  // 3: Karka (Cancer) - Moon
    GrahaName.Sy,  // 4: Simha (Leo) - Sun
    GrahaName.Bu,  // 5: Kanya (Virgo) - Mercury
    GrahaName.Sk,  // 6: Tula (Libra) - Venus
    GrahaName.Ma,  // 7: Vrishchika (Scorpio) - Mars
    GrahaName.Gu,  // 8: Dhanu (Sagittarius) - Jupiter
    GrahaName.Sa,  // 9: Makara (Capricorn) - Saturn
    GrahaName.Sa,  // 10: Kumbha (Aquarius) - Saturn
    GrahaName.Gu   // 11: Meena (Pisces) - Jupiter
  ];

  return rulers[normalizedIndex];
}

/**
 * Determines if a planet (graha) is in its own sign
 * @param graha - Planet name
 * @param longitude - Longitude in decimal degrees
 * @returns Boolean indicating if the planet is in its own sign
 */
export function isInOwnSign(graha: GrahaName, longitude: number): boolean {
  const rashi = getRashi(longitude);
  return RASHI_RULERSHIP[graha]?.includes(rashi) || false;
}